import { Box, IconButton, MenuItem, Switch, TextField, Tooltip, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { horairesParDefaut, JOURS_FR, type Horaires, type Plage } from '@iobiz/shared'

/**
 * Les heures d'ouverture de la boutique.
 *
 * ⭐ C'est d'ici que sort l'agenda : sans ces plages, aucun créneau ne peut
 * être proposé, et l'écran de planning reste une grille vide qu'on ne sait pas
 * expliquer.
 *
 * ⚠️ L'AFFICHAGE commence le lundi, le STOCKAGE le dimanche. Les sept journées
 * sont indexées comme `Date#getDay` — 0 = dimanche — pour qu'aucune conversion
 * ne traîne dans le calcul des créneaux ; mais personne, en français, ne lit une
 * semaine qui commence un dimanche. La table de correspondance vit ici, à
 * l'endroit qui affiche, et nulle part ailleurs.
 */

/** Ordre de LECTURE : lundi → dimanche, en indices « getDay ». */
const ORDRE_AFFICHAGE = [1, 2, 3, 4, 5, 6, 0] as const

const PAS = [10, 15, 20, 30, 45, 60] as const

function Ligne({
  jour,
  horaires,
  onChange,
  onRecopier,
}: {
  jour: number
  horaires: Horaires
  onChange: (h: Horaires) => void
  onRecopier: () => void
}): JSX.Element {
  const journee = horaires.days[jour]!

  function majJournee(patch: Partial<{ closed: boolean; ranges: Plage[] }>): void {
    const days = horaires.days.map((j, i) => (i === jour ? { ...j, ...patch } : j))
    onChange({ ...horaires, days })
  }

  function majPlage(index: number, champ: 'start' | 'end', valeur: string): void {
    majJournee({
      ranges: journee.ranges.map((p, i) => (i === index ? { ...p, [champ]: valeur } : p)),
    })
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '110px 1fr' },
        gap: 2,
        alignItems: 'start',
        py: 2,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Switch
          size="small"
          checked={!journee.closed}
          onChange={(e) =>
            majJournee({
              closed: !e.target.checked,
              // Rouvrir un jour vide sans plage donnerait une journée « ouverte »
              // et pourtant sans un seul créneau : on repose l'amplitude type.
              ranges:
                e.target.checked && journee.ranges.length === 0
                  ? [{ start: '09:00', end: '18:00' }]
                  : journee.ranges,
            })
          }
          inputProps={{ 'aria-label': `Ouvert le ${JOURS_FR[jour]}` }}
        />
        <Typography variant="body2" sx={{ fontWeight: journee.closed ? 400 : 600 }}>
          {JOURS_FR[jour]}
        </Typography>
      </Box>

      {journee.closed ? (
        <Typography variant="body2" sx={{ color: 'text.disabled', pt: 1 }}>
          Fermé
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {journee.ranges.map((plage, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                type="time"
                size="small"
                label="De"
                value={plage.start}
                onChange={(e) => majPlage(i, 'start', e.target.value)}
                sx={{ width: 130 }}
              />
              <TextField
                type="time"
                size="small"
                label="À"
                value={plage.end}
                onChange={(e) => majPlage(i, 'end', e.target.value)}
                error={plage.end <= plage.start}
                helperText={plage.end <= plage.start ? 'Après le début' : ' '}
                sx={{ width: 130 }}
              />
              <IconButton
                size="small"
                aria-label={`Retirer la plage ${i + 1} du ${JOURS_FR[jour]}`}
                onClick={() => majJournee({ ranges: journee.ranges.filter((_, j) => j !== i) })}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* ⭐ Plusieurs plages : la pause de midi est la règle. Sans elle,
                on proposerait des rendez-vous à 13 h dans une boutique fermée. */}
            {journee.ranges.length < 4 && (
              <IconButton
                size="small"
                aria-label={`Ajouter une plage le ${JOURS_FR[jour]}`}
                onClick={() =>
                  majJournee({ ranges: [...journee.ranges, { start: '14:00', end: '18:00' }] })
                }
              >
                <AddIcon fontSize="small" />
              </IconButton>
            )}
            <Tooltip title="Appliquer ces horaires à tous les jours ouverts">
              <IconButton
                size="small"
                aria-label={`Recopier les horaires du ${JOURS_FR[jour]}`}
                onClick={onRecopier}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export function HorairesOuverture({
  valeur,
  onChange,
}: {
  valeur: Horaires | null
  onChange: (h: Horaires) => void
}): JSX.Element {
  const horaires = valeur ?? horairesParDefaut()

  return (
    <Box sx={{ gridColumn: '1 / -1' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Les créneaux de rendez-vous sont proposés à l’intérieur de ces plages, et nulle part
        ailleurs.
      </Typography>

      {ORDRE_AFFICHAGE.map((jour) => (
        <Ligne
          key={jour}
          jour={jour}
          horaires={horaires}
          onChange={onChange}
          onRecopier={() => {
            // ⚠️ On ne recopie que sur les jours OUVERTS : appliquer partout
            // rouvrirait le dimanche d'un salon qui ferme ce jour-là, en un
            // clic destiné à faire gagner du temps.
            const modele = horaires.days[jour]!
            onChange({
              ...horaires,
              days: horaires.days.map((j) => (j.closed ? j : { ...j, ranges: modele.ranges })),
            })
          }}
        />
      ))}

      <TextField
        select
        size="small"
        label="Un rendez-vous peut commencer toutes les"
        value={horaires.slotMinutes}
        onChange={(e) => onChange({ ...horaires, slotMinutes: Number(e.target.value) })}
        helperText="Le pas de la grille — à ne pas confondre avec la durée d’une prestation."
        sx={{ mt: 3, minWidth: 280 }}
      >
        {PAS.map((p) => (
          <MenuItem key={p} value={p}>
            {p} minutes
          </MenuItem>
        ))}
      </TextField>
    </Box>
  )
}
